import { useCallback } from 'react';
import { useMutation } from '@apollo/client';

import useToast from '../../../../../../components/ToastProvider/useToast';
import { CompanyInvitationEmailUpdatedFragmentFragmentDoc } from '../../graphql/fragments.gql';
import { SendCompanyInvitationDocument } from '../../graphql/mutations/sendBeneficiaryInvitation.gql';
import { UpdateCompanyInvitationEmailDocument } from '../../graphql/mutations/updateCompanyInvitationEmail.gql';
import { Beneficiary } from '../../types';
import locales from './locales';

const useInvitationActions = ({
  beneficiary,
}: {
  beneficiary: Beneficiary;
}) => {
  const { showToast } = useToast();

  const [sendInvitation, { loading: isSendingInvitation }] = useMutation(
    SendCompanyInvitationDocument,
  );

  const [updateInvitationEmail, { loading: isUpdatingInvitationEmail }] =
    useMutation(UpdateCompanyInvitationEmailDocument);

  const showSuccessToast = useCallback(() => {
    showToast({
      message: `${locales.formatString(locales.linkResendInvitationSuccess, {
        name: `${beneficiary.firstName} ${beneficiary.lastName}`,
      })}`,
      type: 'success',
    });
  }, [beneficiary, showToast]);

  const resend = useCallback(async () => {
    if (!beneficiary.invitation) {
      throw new Error('Beneficiary unexpectedly missing the invitation field');
    }

    const { companyProfileId, invitation } = beneficiary;
    const { companyInvitationId } = invitation;

    await sendInvitation({
      variables: {
        input: {
          companyInvitationId,
          companyProfileId,
        },
      },
    });

    showSuccessToast();
  }, [showSuccessToast, sendInvitation, beneficiary]);

  const updateEmail = useCallback(
    async (inviteeEmail: string) => {
      if (!beneficiary.invitation) {
        throw new Error(
          'Beneficiary unexpectedly missing the invitation field',
        );
      }

      const { companyProfileId, invitation } = beneficiary;
      const { companyInvitationId } = invitation;

      await updateInvitationEmail({
        // When invitation's email address is updated by the companies-service, the
        // lastEmailSentAt field is set to null. The automatic sending of a new email
        // is done asynchronously in the company-invitations-emails-function.
        // Because of this, the `updateCompanyInvitationEmailAddress` mutation weirdly
        // returns `canResendInvite` to true, so we override it in the cache.
        update: (cache) => {
          cache.writeFragment({
            data: {
              __typename: 'CompanyInvitation',
              canResendInvite: false,
              companyInvitationId,
              inviteeEmail,
            },
            fragment: CompanyInvitationEmailUpdatedFragmentFragmentDoc,
          });
        },

        variables: {
          input: {
            companyInvitationId,
            companyProfileId,
            inviteeEmail,
          },
        },
      });

      showSuccessToast();
    },
    [showSuccessToast, updateInvitationEmail, beneficiary],
  );

  return {
    loading: isSendingInvitation || isUpdatingInvitationEmail,
    resend,
    updateEmail,
  };
};

export default useInvitationActions;
